import { useTranslations } from "next-intl";
import { useMemo, useReducer } from "react";

import { type StartTimeFieldValue } from "~/components/start-times";
import { trpcReact } from "~/trpc/client";

import {
  getPassengerCategories,
  validate,
  type ProductId,
  type TourConfig,
} from "./tours";
import { useCart } from "./use-cart";

interface State {
  passengerCategories: { id: string; count: number }[] | null;
  planId: string;
  startTime: { hour: number; minute: number } | null;
}

type Action =
  | {
      type: "update-passenger-count";
      id: string;
      count: number;
      config: TourConfig;
    }
  | { type: "update-start-time"; starTime: { hour: number; minute: number } }
  | { type: "update-plan"; id: string };

export function useTripTourCartLine({
  datoDayId,
  datoTourId,
}: {
  datoTourId: string;
  datoDayId: string;
}) {
  const cart = useCart().cart;
  if (!cart) {
    return;
  }

  return cart.cartLines
    .filter(
      (cartLine) => cartLine.type === "bokun" || cartLine.type === "linktivity",
    )
    .find((cartLine) => {
      return (
        cartLine.lineItem.datoTourId === datoTourId &&
        cartLine.lineItem.datoDayId === datoDayId
      );
    });
}

export function useTripTour({
  datoTourId,
  datoDayId,
  date,
  productId,
  initialData,
  initialPassengerCategories,
}: {
  datoTourId: string;
  datoDayId: string;
  date: Date;
  productId: ProductId;
  initialData: TourConfig;
  initialPassengerCategories: NonNullable<State["passengerCategories"]>;
}) {
  const t = useTranslations("TourDetails");
  const cartLine = useTripTourCartLine({
    datoDayId,
    datoTourId,
  });

  const [state, dispatch] = useReducer(
    (state: State, action: Action): State => {
      switch (action.type) {
        case "update-passenger-count":
          return {
            ...state,
            passengerCategories: action.config.passengerCategories
              .map(({ id }) => {
                const count =
                  state.passengerCategories?.find(
                    (passengerCategory) => passengerCategory.id === id,
                  )?.count ?? 0;
                return id === action.id
                  ? { id, count: action.count }
                  : { id, count };
              })
              .filter(({ count }) => count > 0),
          };
        case "update-plan":
          return { ...state, planId: action.id };
        case "update-start-time":
          return { ...state, startTime: action.starTime };
        default:
          // oxlint-disable-next-line no-unused-expressions
          action satisfies never;
          return state;
      }
    },
    {
      passengerCategories: initialPassengerCategories,
      planId: initialData.plan.id,
      startTime: null,
    } satisfies State,
  );

  const configQuery = trpcReact.tours.config.useQuery(
    {
      productId,
      planId: state.planId,
    },
    { initialData },
  );

  const config = configQuery.data;

  const choicesQuery = trpcReact.tours.choices.useQuery({
    productId,
    start: date,
    end: date,
    passengerCategories: state.passengerCategories ?? [],
    planId: state.planId,
  });

  const passengerCategories = getPassengerCategories(
    state.passengerCategories ?? [],
    cartLine,
    config,
  );

  const choices = choicesQuery.data ?? [];
  const choice = choices.find(
    (choice) => date.valueOf() === choice.date.valueOf(),
  );
  const choiceStartTimes = choice?.startTimes ?? [];
  const stateStartTime = state.startTime;
  const startTime = stateStartTime
    ? choiceStartTimes.find(
        (startTime) =>
          startTime.hour === stateStartTime.hour &&
          startTime.minute === stateStartTime.minute,
      )
    : (choiceStartTimes[0] ?? null);

  const totalPassengers = passengerCategories.reduce<number>(
    (agg, { count }) => agg + count,
    0,
  );

  const startTimes: StartTimeFieldValue[] = useMemo(() => {
    return (
      (choice ? [...choice.startTimes] : null) ??
      config.startTimes.map((startTime) => ({
        ...startTime,
        disabled: true,
      }))
    );
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choicesQuery.isFetching || configQuery.isFetching]);

  return {
    choice,
    choices,
    startTime,
    startTimes,
    passengerCategories,
    planId: state.planId,
    isFetching: choicesQuery.isFetching || configQuery.isFetching,
    dispatch,
    cartLine,
    totalPassengers,
    config,
    errors: validate({
      t,
      totalPassengers,
      maxPassengersPerBooking: config.maxPassengersPerBooking,
      minPassengersPerBooking: config.minPassengersPerBooking,
      totalQuantityAvailable: startTime?.totalQuantityAvailable,
    }),
  };
}

export type TripTour = ReturnType<typeof useTripTour>;
